// ============================================================
// Kasir/Admin panel — Alpine store: inbox bell (session cookie)
// Tidak bergantung Alpine.store('auth') customer JWT.
// ============================================================

import Alpine from 'alpinejs';
import axios from '../../config/axios.js';
import { API_BASE } from '../../config/axios.js';
import {
  loadNotificationSoundPrefs,
  playNotificationSound,
} from '../../utils/notificationSound.js';

function panelPrefix() {
  const raw = document.querySelector('meta[name="panel-url-prefix"]')?.content || '';
  if (!raw) return '';
  return raw.startsWith('/') ? raw.replace(/\/$/, '') : `/${raw}`.replace(/\/$/, '');
}

function resolvePanelUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path) || path.startsWith('uspatih')) return path;
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${panelPrefix()}${p}`;
}

function isKasirPanel() {
  return !!document.querySelector('meta[name="panel-user-id"]')?.content
    || document.getElementById('kasir-app') != null;
}

Alpine.store('kasirNotifications', {
  unread: 0,
  items: [],
  loading: false,
  open: false,
  page: 1,
  lastPage: 1,
  _prevUnread: 0,
  _pollTimer: null,
  _prefsReady: false,
  inboxUrl: '',

  get unreadCount() {
    return this.unread;
  },

  start() {
    if (!isKasirPanel()) return;
    this.inboxUrl = resolvePanelUrl('/notifications') || '/notifications';
    void this.ensureSoundPrefs();
    void this.fetchUnread();
    this.startPolling(35_000);
  },

  async ensureSoundPrefs() {
    if (this._prefsReady) return;
    await loadNotificationSoundPrefs();
    this._prefsReady = true;
  },

  startPolling(ms = 35_000) {
    if (!isKasirPanel()) return;
    if (this._pollTimer) clearInterval(this._pollTimer);
    this._pollTimer = setInterval(() => {
      void this.fetchUnread();
    }, ms);
  },

  async fetchUnread() {
    if (!isKasirPanel()) return;
    try {
      await this.ensureSoundPrefs();
      const { data } = await axios.get(`${API_BASE}/notifications/unread-count`);
      const next = Number(data.data?.count ?? data.data?.unread_count ?? 0) || 0;
      if (next > this._prevUnread && this._prevUnread > 0) {
        playNotificationSound();
      }
      this._prevUnread = next;
      this.unread = next;
    } catch {
      /* silent — session/expired */
    }
  },

  async fetchRecent(limit = 10) {
    return this.fetchList({ page: 1, perPage: limit, force: true });
  },

  /**
   * @param {{ page?: number, unreadOnly?: boolean, perPage?: number, force?: boolean }} opts
   */
  async fetchList(opts = {}) {
    if (!isKasirPanel()) return;
    const page = opts.page ?? 1;
    const perPage = opts.perPage ?? 20;
    const unreadOnly = !!opts.unreadOnly;
    this.loading = true;
    try {
      const params = { per_page: perPage, page };
      if (unreadOnly) params.is_read = 'false';
      const { data } = await axios.get(`${API_BASE}/notifications`, { params });
      this.items = Array.isArray(data.data) ? data.data : [];
      this.page = data.meta?.current_page ?? page;
      this.lastPage = data.meta?.last_page ?? 1;
      if (typeof data.unread_count === 'number') {
        this.unread = data.unread_count;
        this._prevUnread = data.unread_count;
      }
    } catch {
      this.items = [];
    } finally {
      this.loading = false;
    }
  },

  async toggle() {
    this.open = !this.open;
    if (this.open) await this.fetchRecent();
  },

  close() {
    this.open = false;
  },

  async markRead(id) {
    if (!id) return;
    try {
      await axios.post(`${API_BASE}/notifications/${id}/read`);
      const item = this.items.find((n) => String(n.id) === String(id));
      if (item && !item.is_read) {
        item.is_read = true;
        this.unread = Math.max(0, this.unread - 1);
        this._prevUnread = this.unread;
      }
    } catch { /* silent */ }
  },

  async markAllRead() {
    try {
      await axios.post(`${API_BASE}/notifications/read-all`);
      this.items.forEach((n) => { n.is_read = true; });
      this.unread = 0;
      this._prevUnread = 0;
    } catch { /* silent */ }
  },

  resolveHref(item) {
    if (!item) return null;
    const data = item.data || {};
    let target = item.action_url
      || data.panel_url
      || data.url
      || null;

    if (target && (target.startsWith('uspatih') || /^https?:\/\//i.test(target))) {
      // deep link app — map staff panel paths from data
      target = null;
    }

    if (!target && data.order_id) {
      target = `/orders?highlight=${encodeURIComponent(data.order_id)}`;
    }
    if (!target && data.user_id) {
      target = `/users/${encodeURIComponent(data.user_id)}`;
    }
    if (!target && (data.type === 'order' || item.type === 'order_update' || item.type === 'payment' || data.type === 'payment')) {
      target = '/orders';
    }
    if (!target && (data.type === 'system' || item.type === 'system')) {
      target = '/users';
    }

    return resolvePanelUrl(target);
  },

  go(item) {
    if (!item) return;
    if (!item.is_read) void this.markRead(item.id);
    this.close();
    const href = this.resolveHref(item);
    if (href) window.location.href = href;
  },
});

document.addEventListener('alpine:init', () => {
  queueMicrotask(() => {
    Alpine.store('kasirNotifications').start();
  });
});

document.addEventListener('DOMContentLoaded', () => {
  if (window.Alpine?.store) {
    Alpine.store('kasirNotifications').start();
  }
});
